import React, { useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, Spinner, Button } from '@heroui/react';
import { Megaphone, X } from 'lucide-react';
import ReleaseSection from './ReleaseSection.js';
import { useChangelog } from './useChangelog.js';

/**
 * WhatsNewDrawer displays changelog releases in a side drawer.
 * Can be used standalone (fetches its own data) or composed with WhatsNewButton
 * (receives changelog data via props to avoid duplicate API calls).
 */
function WhatsNewDrawer({ isOpen, onClose, changelog: externalChangelog }) {
	const internalChangelog = useChangelog({ skip: !!externalChangelog });
	const { releases, loading, error, markAsSeen, isReleaseNew } =
		externalChangelog ?? internalChangelog;
	const hasMarkedSeen = useRef(false);
	// Ids of the releases that were new when the drawer opened. markAsSeen() flips
	// `isReleaseNew` at once, so the "New" chips vanished while the drawer was still
	// sliding in and the list jumped (only with unread releases). While open, the
	// drawer keeps showing the state it opened with; the next open reads it fresh.
	const [newAtOpen, setNewAtOpen] = useState(null);

	useEffect(() => {
		if (isOpen && !hasMarkedSeen.current && releases.length > 0) {
			hasMarkedSeen.current = true;
			setNewAtOpen(new Set(releases.filter(isReleaseNew).map(release => release.id)));
			markAsSeen();
		}
	}, [isOpen, releases, markAsSeen, isReleaseNew]);

	// Reset seen flag when drawer closes so it marks again on next open
	useEffect(() => {
		if (!isOpen) {
			hasMarkedSeen.current = false;
			setNewAtOpen(null);
		}
	}, [isOpen]);

	const isNew = release => (newAtOpen ? newAtOpen.has(release.id) : isReleaseNew(release));

	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			placement="right"
			size="md"
			/* Hide HeroUI's default ✕ — we render our own 44px close button in the
			   header (≥44px touch target per the responsive contract). */
			hideCloseButton
			/* Legacy `.site-top-menu` has z-index 1052 and would overlap the drawer's
			   top (header + close button) on mobile. Lift the drawer + backdrop above it.
			   `base`: the modal's default radius (rounded-large, all corners) is merged
			   AFTER the right placement's rounded-r-none, so the corners against the
			   page edge were rounded too. Square them; the left corners stay rounded. */
			classNames={{ wrapper: '!z-[1060]', backdrop: '!z-[1060]', base: '!rounded-r-none' }}
		>
			<DrawerContent>
				<DrawerHeader className="flex items-center justify-between gap-2 border-b border-default-200">
					<div className="flex items-center gap-2">
						<Megaphone className="w-5 h-5 text-primary-500" />
						<span>What's New</span>
					</div>
					{/* Explicit close button — the default HeroUI drawer ✕ is not
					    reachable on mobile where the drawer is full-width (no tappable
					    backdrop). 44px touch target per the responsive contract. */}
					<Button
						isIconOnly
						variant="light"
						radius="full"
						aria-label="Close"
						onPress={onClose}
						className="min-w-11 w-11 h-11"
					>
						<X className="w-5 h-5" />
					</Button>
				</DrawerHeader>
				<DrawerBody className="py-4">
					{loading && (
						<div className="flex justify-center items-center py-12">
							<Spinner size="lg" />
						</div>
					)}
					{error && !loading && (
						<div className="text-center py-12">
							<p className="text-default-500 text-sm">Failed to load updates</p>
							<p className="text-default-400 text-xs mt-1">{error}</p>
						</div>
					)}
					{!loading && !error && releases.length === 0 && (
						<div className="text-center py-12">
							<Megaphone className="w-10 h-10 text-default-300 mx-auto mb-3" />
							<p className="text-default-500 text-sm">No updates yet</p>
							<p className="text-default-400 text-xs mt-1">
								Check back later for new features and improvements
							</p>
						</div>
					)}
					{!loading && !error && releases.length > 0 && (
						<div>
							{releases.map((release, index) => (
								<ReleaseSection
									key={release.id}
									release={release}
									isNew={isNew(release)}
									isLast={index === releases.length - 1}
								/>
							))}
						</div>
					)}
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
}

export default WhatsNewDrawer;
